import * as React from "react";
import Downshift from "downshift";
import styled, { css, cx } from "react-emotion";
import { fetchTags, tagsToParams } from "../api/tags";
import { primaryColors, zuerichBlueColors } from "../theme/colors";
import { RemovableTag } from "../components/Tag";
import { strong, small, helvetica } from "../theme/typefaces";
import { Icon } from "../theme/icons";
import { Small } from "../theme/text";
import { spacing, breakpoints } from "../theme/layout";
import { TagList } from "../components/TagList";
import { createFetcher } from "../components/Fetcher";
import { modalOverlayStyle } from "../components/Modals";
const Input = styled("input") `
  ${helvetica};
  ${spacing.pa0};
  color: ${primaryColors.zuerichBlue};
  line-height: 26px;
  height: 26px;
  font-size: 1rem; /* Needs to be at least 16px to prevent zoom on mobile */
  flex: 1 0 0%;
  width: 100%;
  text-overflow: ellipsis;
  background: ${zuerichBlueColors["400"]};
  border: none;
  transition: color ease 0.3s, background ease 0.3s;
  ${spacing.mb1};
  ${spacing.pl1};

  &:focus {
    /* a11y-OK because the field turns white! */
    outline: 0;
    background: ${primaryColors.white};
    &::placeholder {
      color: ${zuerichBlueColors["700"]};
    }
  }

  &::placeholder {
    color: ${primaryColors.zuerichBlue};
  }

  &::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }
  &::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }
`;
const InputGroup = styled("div") `
  display: block;
  background: ${zuerichBlueColors["400"]};
  transition: background ease 0.3s;
  border-radius: 3px;
  ${spacing.pt1};
  ${spacing.pr2};
  ${spacing.pl1};
  ${breakpoints.medium `
    display: flex;
    flex-wrap: wrap;
  `};
`;
const InputFieldWithButton = styled("div") `
  display: flex;
  flex: 1 0 0%;
  min-width: 240px;
`;
const inputGroupFocusStyle = css `
  background: ${primaryColors.white};
`;
const inputGroupWithMenuFocusStyle = css `
  border-radius: 3px 3px 0 0;
`;
const SelectedTags = styled("div") ``;
const MenuContainer = styled("div") `
  position: absolute;
  width: 100%;
  z-index: 10;
`;
const Menu = styled("div") `
  width: 100%;
  box-sizing: border-box;
  background: ${primaryColors.white};
  border-top: 1px solid ${zuerichBlueColors["100"]};
  border-radius: 0 0 3px 3px;
  ${spacing.pa2};
`;
const SearchFieldWrapper = styled("div") `
  position: relative;
`;
/**
 *  Index page overlay
 *  Totaled padding taken from Header- and SearchContainer
 */
const SearchFieldWrapperOverlayIndex = css `
${breakpoints.large `
  ${modalOverlayStyle};
    padding-top: 260px;
    padding-right: 208px;
    padding-left: 208px;
    margin: 0 auto;
  `};
`;
/**
 *  Search results page overlay
 *  padding-top taken from Header
 */
const SearchFieldWrapperOverlayIndexResult = css `
${breakpoints.large `
  ${modalOverlayStyle};
    padding-top: 16px;
    padding-right: 208px;
    padding-left: 208px;
    margin: 0 auto;
  `};
`;
const InputButton = styled("button") `
  ${small};
  ${strong};
  display: inline-block;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  border: none;
  line-height: 26px;
  height: 26px;
  color: ${primaryColors.zuerichBlue};
  font-size: 1em;
  background-color: transparent;
  padding: 0;
  margin: 0;

  cursor: pointer;
`;
const TagItem = styled("span") `
  display: inline-block;
  ${spacing.mr1};
  ${spacing.mb1};
`;
export const AutocompleteSuggestions = ({ items, loading, downshiftProps }) => (<Menu>
    {items.length ? (<TagList limitPerGroup={10} tags={items} getTagProps={(item, index) => ({
    ...downshiftProps.getItemProps({ index, item }),
    appearance: index === downshiftProps.highlightedIndex ? "dark" : "light"
})}/>) : loading ? null : (<Small>Keine Ergebnisse</Small>)}
    {loading && (<div>
        <Small>Lade …</Small>
      </div>)}
  </Menu>);
export class SearchField extends React.Component {
    constructor() {
        super(...arguments);
        this.inputFieldRef = (el) => {
            this.inputField = el;
        };
    }
    componentDidMount() {
        if (this.props.inFocus && this.inputField) {
            this.inputField.focus();
        }
    }
    componentDidUpdate() {
        if (this.props.inFocus && this.inputField) {
            this.inputField.focus();
        }
    }
    render() {
        const { selectedItems, items, onChange, onInputFocus, onInputBlur, inFocus, loading, placeholderText, onSelect, onRemoveTag } = this.props;
        return (<Downshift id="search" onInputValueChange={query => onChange({ query, tags: selectedItems })} itemToString={(item) => (item ? item["@id"] : "")} selectedItem={null} defaultHighlightedIndex={0} onSelect={onSelect} isOpen={inFocus} stateReducer={(state, change) => {
            if (change.selectedItem) {
                // Reset inputValue when an item has been selected
                return { ...change, inputValue: "" };
            }
            return change;
        }} render={props => {
            return (<div>
              <SearchFieldWrapper>
                <InputGroup className={cx({
                [inputGroupFocusStyle]: inFocus,
                [inputGroupWithMenuFocusStyle]: props.inputValue !== ""
            })}>
                  <SelectedTags>
                    {selectedItems &&
                selectedItems.map(item => (<TagItem key={item["@id"]}>
                          <RemovableTag appearance={inFocus ? "dark" : "inverted"} tagType={item["@type"]} label={item.label} onClick={() => onRemoveTag(item)}/>
                        </TagItem>))}
                  </SelectedTags>
                  <InputFieldWithButton>
                    <Input {...props.getInputProps({
                placeholder: placeholderText,
                id: "search-input"
            })} innerRef={this.inputFieldRef} onFocus={onInputFocus} onBlur={onInputBlur}/>
                    <div className={css `
                        color: ${primaryColors.zuerichBlue};
                        line-height: 26px;
                        height: 26px;
                        margin-left: 6px;
                      `}>
                      {props.isOpen ? (<InputButton {...props.getButtonProps({
                "aria-label": "Menü schliessen"
            })}>
                          <Icon name="Cross"/>
                        </InputButton>) : (<InputButton {...props.getButtonProps({
                "aria-label": "Menü öffnen",
                onClick: () => onInputFocus()
            })}>
                          <Icon name="Search"/>
                        </InputButton>)}
                    </div>
                  </InputFieldWithButton>
                </InputGroup>
                {props.isOpen && props.inputValue !== "" ? (<MenuContainer>
                    <AutocompleteSuggestions items={items} downshiftProps={props} loading={loading}/>
                  </MenuContainer>) : null}
              </SearchFieldWrapper>
            </div>);
        }}/>);
    }
}
const TagsFetcher = createFetcher({ fetch: fetchTags });
/**
 * The SearchController handles fetching of tags for autocomplete and all temporary state for the
 * Search field but doesn't manage the selection of tags themselves.
 */
export class SearchController extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            query: "",
            selectedItems: [],
            items: [],
            loading: false,
            inFocus: false
        };
        this.handleFocus = () => this.setState({ inFocus: true });
        this.handleBlur = () => this.setState({ inFocus: false });
        this.handleChange = (input) => {
            // prevent http request when a tag is selected
            if (/^http/.test(input.query)) {
                return;
            }
            this.setState({ query: input.query });
        };
        this.handleSelect = (t) => {
            this.props.addSearchTag(t);
            this.setState({ query: "" });
        };
        this.handleRemove = (t) => {
            this.props.removeSearchTag(t);
        };
    }
    render() {
        let cls = this.props.page === "index" && this.state.inFocus && this.state.query !== "" ?
            this.props.selectedTags.length === 0 ?
                SearchFieldWrapperOverlayIndex :
                SearchFieldWrapperOverlayIndexResult :
            "";
        return (<div>
        <TagsFetcher fetchOnMount={false} fetchArg={{
            query: this.state.query,
            ...tagsToParams(this.props.selectedTags)
        }}>
          {res => {
            return (<div className={cls}>
                <SearchField selectedItems={this.props.selectedTags} items={res.data ? res.data : []} loading={res.loading} onChange={this.handleChange} onInputFocus={this.handleFocus} onInputBlur={this.handleBlur} inFocus={this.state.inFocus} onSelect={this.handleSelect} onRemoveTag={this.handleRemove} placeholderText="Suchbegriff eingeben, z.B. Bevölkerung"/>
              </div>);
        }}
        </TagsFetcher>
      </div>);
    }
}



// WEBPACK FOOTER //
// ./src/search/Search.tsx